import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { useToast } from "../../hooks/use-toast";
import { cn } from "../../lib/utils";
import { Plus, Loader2, FileText, Upload, X, CheckCircle2, Download } from "lucide-react";

function dueDateInfo(dueDate) {
  if (!dueDate) return null;
  const days = Math.round((new Date(dueDate) - new Date(new Date().toISOString().slice(0, 10))) / 86400000);
  if (days < 0) return { label: `Overdue by ${Math.abs(days)}d`, cls: "text-red-600" };
  if (days <= 3) return { label: days === 0 ? "Due today" : `Due in ${days}d`, cls: "text-amber-600" };
  return { label: `Due ${dueDate}`, cls: "text-muted-foreground" };
}

function AddPolicyModal({ open, onClose, onSaved }) {
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [category, setCategory] = useState("Security");
  const [owner, setOwner] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast({ title: "Name is required", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/compliance/policies", { name, category, owner: owner || null });
      toast({ title: "Policy created" });
      setName(""); setOwner("");
      onSaved?.();
    } catch (e) {
      toast({ title: "Couldn't create policy", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md" data-testid="compliance-policy-modal">
        <DialogHeader><DialogTitle>New policy</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" autoFocus data-testid="compliance-policy-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Input value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1" data-testid="compliance-policy-category-input" />
            </div>
            <div>
              <Label>Owner</Label>
              <Input value={owner} onChange={(e) => setOwner(e.target.value)} className="mt-1" data-testid="compliance-policy-owner-input" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="compliance-policy-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={busy} className="gap-1.5" data-testid="compliance-policy-save-btn">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {busy ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PolicyDetailDrawer({ policy, onClose, isAdmin, onChanged }) {
  const { toast } = useToast();
  const [acks, setAcks] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(false);
  const [busyPersonId, setBusyPersonId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [acksRes, peopleRes] = await Promise.all([
        api.get(`/compliance/policies/${policy.id}/acknowledgments`),
        api.get("/compliance/people"),
      ]);
      setAcks(acksRes.data.acknowledgments || []);
      setPeople(peopleRes.data.people || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, [policy.id]);
  useEffect(() => { load(); }, [load]);

  const assignedIds = new Set(acks.map((a) => a.person_id));
  const unassigned = people.filter((p) => !assignedIds.has(p.id));

  const toggleSign = async (ack) => {
    setBusyPersonId(ack.person_id);
    try {
      await api.put(`/compliance/policies/${policy.id}/acknowledgments/${ack.person_id}`, {
        status: ack.status === "signed" ? "pending" : "signed",
      });
      await load();
      onChanged?.();
    } catch (e) {
      toast({ title: "Couldn't update sign-off", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusyPersonId(null); }
  };

  const removeAssignment = async (ack) => {
    setBusyPersonId(ack.person_id);
    try {
      await api.delete(`/compliance/policies/${policy.id}/acknowledgments/${ack.person_id}`);
      await load();
      onChanged?.();
    } catch (e) {
      toast({ title: "Couldn't remove", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBusyPersonId(null); }
  };

  const assignPicked = async () => {
    if (picked.length === 0) return;
    setAssigning(true);
    try {
      await api.post(`/compliance/policies/${policy.id}/assign`, { person_ids: picked });
      toast({ title: `Assigned to ${picked.length} ${picked.length === 1 ? "person" : "people"}` });
      setPicked([]);
      await load();
      onChanged?.();
    } catch (e) {
      toast({ title: "Couldn't assign", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setAssigning(false); }
  };

  const assignEveryone = async () => {
    if (unassigned.length === 0) return;
    setAssigning(true);
    try {
      await api.post(`/compliance/policies/${policy.id}/assign`, { person_ids: unassigned.map((p) => p.id) });
      toast({ title: `Rolled out to the whole team (${unassigned.length} ${unassigned.length === 1 ? "person" : "people"})` });
      setPicked([]);
      await load();
      onChanged?.();
    } catch (e) {
      toast({ title: "Couldn't assign", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setAssigning(false); }
  };

  const uploadDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      await api.post(`/compliance/policies/${policy.id}/document`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast({ title: "Document uploaded" });
      onChanged?.();
    } catch (err) {
      toast({ title: "Upload failed", description: err?.response?.data?.detail || "", variant: "destructive" });
    } finally { setUploading(false); e.target.value = ""; }
  };

  const viewDocument = async () => {
    setViewingDoc(true);
    try {
      const res = await api.get(`/compliance/policies/${policy.id}/document`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast({ title: "Couldn't open document", variant: "destructive" });
    } finally { setViewingDoc(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-lg" data-testid="compliance-policy-detail-drawer">
        <DialogHeader>
          <DialogTitle>{policy.name} — Sign-off</DialogTitle>
          <DialogDescription>View the policy document, see who it's assigned to, and track sign-off status.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1 max-h-[70vh] overflow-y-auto">
          {/* Document */}
          <div className="rounded-lg border border-border p-3">
            <div className="text-[11px] font-semibold uppercase text-muted-foreground mb-2">Policy document</div>
            {policy.document_name ? (
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-[12.5px] min-w-0">
                  <FileText className="w-4 h-4 text-primary shrink-0" />
                  <span className="truncate">{policy.document_name}</span>
                </div>
                <Button size="sm" variant="outline" onClick={viewDocument} disabled={viewingDoc} data-testid="compliance-policy-view-document-btn">
                  {viewingDoc ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "View"}
                </Button>
              </div>
            ) : (
              <div className="text-[12px] text-muted-foreground">No document on file yet.</div>
            )}
            {isAdmin && (
              <label className="mt-2 flex items-center gap-1.5 text-[11.5px] font-medium text-primary hover:underline cursor-pointer w-fit" data-testid="compliance-policy-upload-document-label">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {policy.document_name ? "Replace document" : "Upload document"}
                <input type="file" accept=".pdf,.doc,.docx,.txt" className="hidden" onChange={uploadDocument} disabled={uploading} data-testid="compliance-policy-upload-document-input" />
              </label>
            )}
          </div>

          {/* Assigned people */}
          <div>
            <div className="text-[11px] font-semibold uppercase text-muted-foreground mb-2">
              Assigned ({acks.filter((a) => a.status === "signed").length}/{acks.length} signed)
            </div>
            {loading ? (
              <div className="h-16 rounded-lg bg-muted/40 animate-pulse" />
            ) : acks.length === 0 ? (
              <div className="text-[12px] text-muted-foreground">Not assigned to anyone yet.</div>
            ) : (
              <div className="space-y-1.5">
                {acks.map((a) => (
                  <div key={a.person_id} className="flex items-center justify-between gap-2 rounded-lg border border-border px-2.5 py-1.5" data-testid={`compliance-policy-ack-row-${a.person_id}`}>
                    <div className="min-w-0">
                      <span className="text-[12.5px] font-medium truncate block">{a.person_name}</span>
                      {a.status === "pending" && dueDateInfo(a.due_date) && (
                        <span className={cn("text-[10.5px] font-medium", dueDateInfo(a.due_date).cls)} data-testid={`compliance-policy-ack-due-${a.person_id}`}>
                          {dueDateInfo(a.due_date).label}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={cn(
                        "text-[10px] font-semibold uppercase rounded-full border px-2 py-0.5",
                        a.status === "signed" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600" : "border-amber-500/40 bg-amber-500/10 text-amber-600"
                      )}>{a.status === "signed" ? "Signed" : "Pending"}</span>
                      {isAdmin && (
                        <>
                          <button
                            type="button"
                            onClick={() => toggleSign(a)}
                            disabled={busyPersonId === a.person_id}
                            className="text-[11px] font-medium text-primary hover:underline disabled:opacity-50"
                            data-testid={`compliance-policy-ack-toggle-${a.person_id}`}
                          >
                            {a.status === "signed" ? "Mark pending" : "Mark signed"}
                          </button>
                          <button
                            type="button"
                            onClick={() => removeAssignment(a)}
                            disabled={busyPersonId === a.person_id}
                            className="text-muted-foreground hover:text-destructive disabled:opacity-50"
                            data-testid={`compliance-policy-ack-remove-${a.person_id}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Assign more people */}
          {isAdmin && unassigned.length > 0 && (
            <div className="rounded-lg border border-border p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] font-semibold uppercase text-muted-foreground">Assign to people</div>
                <button
                  type="button"
                  onClick={assignEveryone}
                  disabled={assigning}
                  className="text-[11px] font-medium text-primary hover:underline disabled:opacity-50"
                  data-testid="compliance-policy-assign-everyone-btn"
                >
                  Assign to everyone ({unassigned.length})
                </button>
              </div>
              <div className="space-y-1.5 max-h-36 overflow-y-auto">
                {unassigned.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-[12.5px] cursor-pointer" data-testid={`compliance-policy-assign-option-${p.id}`}>
                    <Checkbox
                      checked={picked.includes(p.id)}
                      onCheckedChange={(v) => setPicked((prev) => v ? [...prev, p.id] : prev.filter((id) => id !== p.id))}
                    />
                    {p.name}
                  </label>
                ))}
              </div>
              <Button size="sm" className="mt-2 gap-1.5" onClick={assignPicked} disabled={assigning || picked.length === 0} data-testid="compliance-policy-assign-btn">
                {assigning && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {assigning ? "Assigning…" : `Assign (${picked.length})`}
              </Button>

            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="compliance-policy-detail-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CompliancePolicies({ isAdmin }) {
  const { toast } = useToast();
  const [policies, setPolicies] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [bulkAssigningId, setBulkAssigningId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [policiesRes, peopleRes] = await Promise.all([
        api.get("/compliance/policies"),
        api.get("/compliance/people"),
      ]);
      setPolicies(policiesRes.data.policies || []);
      setPeople(peopleRes.data.people || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Keep the open drawer's policy prop fresh (e.g. after a document upload) without
  // requiring the user to close and reopen it.
  useEffect(() => {
    if (!selected) return;
    const updated = policies.find((p) => p.id === selected.id);
    if (updated) setSelected(updated);
  }, [policies]); // eslint-disable-line react-hooks/exhaustive-deps

  const publish = async (p) => {
    try {
      await api.put(`/compliance/policies/${p.id}`, { status: "published" });
      toast({ title: "Policy published" });
      load();
    } catch (e) {
      toast({ title: "Couldn't publish", description: e?.response?.data?.detail || "", variant: "destructive" });
    }
  };

  const assignToEveryone = async (p) => {
    setBulkAssigningId(p.id);
    try {
      await api.post(`/compliance/policies/${p.id}/assign`, { person_ids: people.map((person) => person.id) });
      toast({ title: `${p.name} rolled out to the whole team` });
      load();
    } catch (e) {
      toast({ title: "Couldn't assign", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setBulkAssigningId(null); }
  };

  const exportAuditTrail = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/compliance/policies/acknowledgments/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `policy-signoff-history-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: "Export failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setExporting(false); }
  };

  if (loading) return <div className="h-64 rounded-xl bg-muted/40 animate-pulse" />;

  return (
    <div className="space-y-3" data-testid="compliance-policies-page">
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" className="gap-1.5" onClick={exportAuditTrail} disabled={exporting} data-testid="compliance-policy-export-audit-trail-btn">
          {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          Export audit trail
        </Button>
        {isAdmin && (
          <Button size="sm" className="gap-1.5" onClick={() => setModalOpen(true)} data-testid="compliance-policy-add-btn">
            <Plus className="w-3.5 h-3.5" /> New policy
          </Button>
        )}
      </div>
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <Table data-testid="compliance-policies-table">
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Policy</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Version</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Document</TableHead>
              <TableHead>Acknowledged</TableHead>
              <TableHead>Review date</TableHead>
              {isAdmin && <TableHead />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {policies.map((p) => (
              <TableRow
                key={p.id}
                data-testid={`compliance-policy-row-${p.id}`}
                className="cursor-pointer hover:bg-muted/40"
                onClick={() => setSelected(p)}
              >
                <TableCell className="font-mono text-[12px]">{p.code}</TableCell>
                <TableCell className="text-[12.5px]">{p.name}</TableCell>
                <TableCell className="text-[12px] text-muted-foreground">{p.category}</TableCell>
                <TableCell className="text-[12px]">{p.owner || "—"}</TableCell>
                <TableCell className="text-[12px]">{p.version}</TableCell>
                <TableCell>
                  <span className={cn(
                    "text-[10.5px] font-semibold uppercase rounded-full border px-2 py-0.5",
                    p.status === "published" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600" : "border-muted-foreground/30 text-muted-foreground"
                  )}>{p.status}</span>
                </TableCell>
                <TableCell>
                  {p.document_name ? (
                    <span className="inline-flex items-center gap-1 text-[11.5px] text-emerald-600"><CheckCircle2 className="w-3.5 h-3.5" /> Uploaded</span>
                  ) : (
                    <span className="text-[11.5px] text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-[12px]">{p.ack}/{p.total}</TableCell>
                <TableCell className="text-[12px] text-muted-foreground">{p.review_date || "—"}</TableCell>
                {isAdmin && (
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {p.status !== "published" && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); publish(p); }}
                          className="text-[11.5px] font-medium text-primary hover:underline"
                          data-testid={`compliance-policy-publish-${p.id}`}
                        >Publish</button>
                      )}
                      {people.length > 0 && p.total < people.length && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); assignToEveryone(p); }}
                          disabled={bulkAssigningId === p.id}
                          className="text-[11.5px] font-medium text-primary hover:underline disabled:opacity-50"
                          data-testid={`compliance-policy-assign-everyone-row-${p.id}`}
                        >{bulkAssigningId === p.id ? "Assigning…" : "Assign to everyone"}</button>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>

            ))}
            {policies.length === 0 && (
              <TableRow><TableCell colSpan={isAdmin ? 10 : 9} className="text-center py-10" data-testid="compliance-policies-empty">
                <div className="text-[13px] font-medium">No policies yet</div>
                <div className="text-[12px] text-muted-foreground mt-1">Click "New policy" above to add your first one, upload its document, and assign it to your team for sign-off.</div>
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <AddPolicyModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={() => { setModalOpen(false); load(); }} />
      {selected && (
        <PolicyDetailDrawer
          policy={selected}
          isAdmin={isAdmin}
          onClose={() => setSelected(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}
